import Footer from '@/components/Footer';
import Header from '@/components/Header';
import SparkleTrail, { useTrailEffect } from '@/components/SparkleTrail';
import { fadeIn, slideUp } from '@/utils/animations';
import { motion } from 'framer-motion';
import { Award, BookOpen, Code2, GraduationCap, School, Users } from "lucide-react";

const container = {
   hidden: { opacity: 0 },
   visible: {
      opacity: 1,
      transition: {
         staggerChildren: 0.2
      }
   }
};

const CreditsPage = () => {
   return (
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-green-50 to-white">
         <Header />
         <motion.main
            className="flex-1 mb-14"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
         >
            <div className="container mx-auto px-4 py-16">
               <motion.h1
                  className="text-5xl font-bold text-green-800 mb-16 text-center"
                  variants={slideUp}
               >
                  Meet the Team
               </motion.h1>

               {/* Project Information */}
               <motion.div
                  className="mb-24 bg-white rounded-2xl shadow-xl p-10 hover:shadow-2xl transition-shadow duration-300"
                  variants={fadeIn}
               >
                  <div className="flex items-center justify-center mb-8">
                     <Code2 className="h-12 w-12 text-green-600 mr-4" />
                     <h2 className="text-3xl font-bold text-green-800">PermitTree</h2>
                  </div>
                  <p className="text-lg text-center text-gray-600 mb-8">
                     A Capstone Project developed for DENR-PENRO Marinduque's Tree Permit Management System
                  </p>
                  <div className="grid md:grid-cols-3 gap-8 text-center">
                     <div className="p-6 bg-green-50 rounded-lg">
                        <School className="h-10 w-10 text-green-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-green-800 mb-2">Institution</h3>
                        <p className="text-gray-600">Marinduque State College</p>
                        <p className="text-gray-600">College of Information and Computing Sciences</p>
                     </div>
                     <div className="p-6 bg-green-50 rounded-lg">
                        <BookOpen className="h-10 w-10 text-green-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-green-800 mb-2">Course</h3>
                        <p className="text-gray-600">Bachelor of Science in Information Technology</p>
                        <p className="text-gray-600">Batch 2025</p>
                     </div>
                     <div className="p-6 bg-green-50 rounded-lg">
                        <Award className="h-10 w-10 text-green-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-green-800 mb-2">Project Type</h3>
                        <p className="text-gray-600">Capstone Project</p>
                        <p className="text-gray-600">AY 2024-2025</p>
                     </div>
                  </div>
               </motion.div>

               {/* Project Team */}
               <motion.div
                  className="mb-24"
                  variants={container}
                  initial="hidden"
                  animate="visible"
               >
                  <div className="flex items-center justify-center mb-12">
                     <Users className="h-14 w-14 text-green-600 mr-4" />
                     <h2 className="text-4xl font-bold text-green-800">Project Team</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-10">
                     {[
                        {
                           image: "https://avatars.githubusercontent.com/u/110120891?v=4",
                           name: "Mark Gilbert Reginio",
                           role: "Project Lead & Full Stack Developer",
                           tasks: [
                              "System Architecture & Development",
                              "Database Design & Implementation",
                              "UI/UX Design",
                              "Documentation"
                           ]
                        },
                        {
                           image: "/images/donna.jpg",
                           name: "Ma. Donna Fidelino",
                           role: "Quality Assurance & Documentation",
                           tasks: [
                              "System Testing & QA",
                              "User Documentation",
                              "Research & Analysis",
                              "Content Management"
                           ]
                        },
                        {
                           image: "/images/ahmad.jpg",
                           name: "Ahmad Magdalita",
                           role: "Project Leader & Documentation",
                           tasks: [
                              "Project Management",
                              "Documentation Lead",
                              "Research & Analysis",
                              "Requirements Gathering"
                           ]
                        },
                        {
                           image: "/images/largo.jpg",
                           name: "Mark Joseph Largo",
                           role: "Documentation & Research",
                           tasks: [
                              "Technical Documentation",
                              "Research Support",
                              "Requirements Analysis"
                           ]
                        }
                     ].map((member, index) => {
                        const isProjectLead = member.role === "Project Lead & Full Stack Developer";
                        const trailEffect = isProjectLead ? useTrailEffect(`member-${index}`) : null;

                        return (
                           <motion.div
                              key={index}
                              className="group relative overflow-hidden"
                              variants={fadeIn}
                              whileHover={{ scale: 1.02 }}
                              {...(isProjectLead && {
                                 onMouseEnter: trailEffect?.handleMouseEnter,
                                 onMouseLeave: trailEffect?.handleMouseLeave,
                                 onMouseMove: trailEffect?.handleMouseMove,
                              })}
                           >
                              {isProjectLead && trailEffect && (
                                 <SparkleTrail
                                    sparkles={trailEffect.sparkles}
                                    onComplete={trailEffect.removeSparkle}
                                 />
                              )}
                              <div className="text-center p-8 bg-white rounded-2xl transition-all duration-300">
                                 <div className="relative mb-6 inline-block">
                                    <div className="absolute inset-0 bg-green-200 rounded-full transform scale-110 group-hover:scale-125 transition-transform duration-300 -z-10"></div>
                                    <img
                                       src={member.image}
                                       alt={member.name}
                                       className="w-40 h-40 rounded-full mx-auto border-4 border-green-600 object-cover"
                                    />
                                 </div>
                                 <h3 className="text-2xl font-bold text-green-800 mb-3">{member.name}</h3>
                                 <p className="text-lg text-green-600 font-medium mb-4">{member.role}</p>
                                 <ul className="text-gray-600 space-y-2">
                                    {member.tasks.map((task, i) => (
                                       <li key={i} className="flex items-center justify-center">
                                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                          {task}
                                       </li>
                                    ))}
                                 </ul>
                              </div>
                           </motion.div>
                        );
                     })}
                  </div>
               </motion.div>

               {/* Project Adviser */}
               <motion.div
                  className="mb-24 bg-white rounded-2xl shadow-xl p-10 hover:shadow-2xl transition-shadow duration-300"
                  variants={fadeIn}
               >
                  <div className="flex items-center justify-center mb-8">
                     <GraduationCap className="h-12 w-12 text-green-600 mr-4" />
                     <h2 className="text-3xl font-bold text-green-800">Project Adviser</h2>
                  </div>
                  <div className="text-center max-w-2xl mx-auto">
                     <h3 className="text-2xl font-semibold text-green-800 mb-2">[Adviser's Name]</h3>
                     <p className="text-gray-600 mb-4">Dean, College of Information and Computing Sciences</p>
                     <p className="text-gray-600 italic">
                        "Thank you for your invaluable guidance and support throughout the development of this project."
                     </p>
                  </div>
               </motion.div>

               {/* System Information */}
               <motion.div
                  className="bg-white rounded-2xl shadow-xl p-10 hover:shadow-2xl transition-shadow duration-300"
                  variants={fadeIn}
               >
                  <h2 className="text-2xl font-bold text-green-800 mb-6 text-center">Technical Overview</h2>
                  <div className="grid md:grid-cols-2 gap-8">
                     {/* Development Statistics Column */}
                     <div className="text-center">
                        <h3 className="text-xl font-semibold text-green-700 mb-4">Development Statistics</h3>
                        <ul className="space-y-2 text-gray-600">
                           <li>• Development Period: 7 months</li>
                           <li>• Total Lines of Code: 137,403</li>
                           <li>• Optimized Code: 93,724 lines</li>
                           <li>• Number of Files: 483</li>
                        </ul>
                     </div>
                     {/* Technologies Used Column */}
                     <div className="text-center">
                        <h3 className="text-xl font-semibold text-green-700 mb-4">Technologies Used</h3>
                        <ul className="space-y-2 text-gray-600">
                           <li>• Frontend: React.js, TailwindCSS</li>
                           <li>• Backend: Node.js, GraphQL</li>
                           <li>• Database: MongoDB Atlas</li>
                           <li>• Deployment: Vercel</li>
                        </ul>
                     </div>
                  </div>
               </motion.div>
            </div>
         </motion.main>
         <Footer />
      </div>
   );
};

export default CreditsPage;